/**
 * Island / Stage Select screen.
 *
 * One per letter. Shows the letter large at the top and the 5 stage
 * buttons arranged as a path up the island. Each stage button displays
 * 0-3 stars earned. Completed stages show a green check; not-yet-played
 * stages show the stage icon.
 *
 * Stage 5 (Word Garden) requires at least one star from stages 1-4 to
 * unlock. All other stages are unlocked from day one.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { api } from '../lib/api';
import {
  playUrl,
  unlockAudio,
  letterNameUrl,
  letterSoundUrl,
  prefetchAudio,
} from '../lib/audio';
import type { LetterSummary } from '../state/content';
import { currentUser } from '../state/user';
import { IslandSkeleton } from '../components/Skeleton';
import { NavButton } from '../components/NavButton';
import type { Word } from '@huroof/shared';

interface StageProgress {
  letter_id: number;
  stage: number;
  stars: number;
}

/**
 * Per-stage visual identity.
 * - `gradient` sets a soft diagonal background when the stage hasn't been
 *   played yet (white-to-tone). Completed stages stay on the solid leaf
 *   green that signals "done" at a glance.
 * - `accent` is the colour of the icon chip, the bottom ribbon, and the
 *   decorative corner shape.
 */
interface StageMeta {
  id: number;
  icon: string;
  label_ar: string;
  label_en: string;
  path: string;
  gradient: string;
  accent: string;
  accentRing: string;
  shape: 'eye' | 'lines' | 'waves' | 'bubble' | 'seed';
}

const STAGES: readonly StageMeta[] = [
  {
    id: 1,
    icon: '👁',
    label_ar: 'تَعَرَّفْ',
    label_en: 'Meet',
    path: 'meet',
    gradient: 'from-white to-ocean/10',
    accent: 'bg-ocean text-white',
    accentRing: 'ring-ocean/30',
    shape: 'eye',
  },
  {
    id: 2,
    icon: '✏️',
    label_ar: 'اِرْسُمْ',
    label_en: 'Draw',
    path: 'draw',
    gradient: 'from-white to-sun/20',
    accent: 'bg-sun text-ink',
    accentRing: 'ring-sun/40',
    shape: 'lines',
  },
  {
    id: 3,
    icon: '👂',
    label_ar: 'اِسْمَعْ',
    label_en: 'Hear',
    path: 'hear',
    gradient: 'from-white to-coral/10',
    accent: 'bg-coral text-white',
    accentRing: 'ring-coral/30',
    shape: 'waves',
  },
  {
    id: 4,
    icon: '🗣',
    label_ar: 'قُلْ',
    label_en: 'Say',
    path: 'say',
    gradient: 'from-white to-berry/10',
    accent: 'bg-berry text-white',
    accentRing: 'ring-berry/30',
    shape: 'bubble',
  },
  {
    id: 5,
    icon: '🌱',
    label_ar: 'حَدِيقَة',
    label_en: 'Garden',
    path: 'garden',
    gradient: 'from-white to-leaf/15',
    accent: 'bg-leaf text-white',
    accentRing: 'ring-leaf/30',
    shape: 'seed',
  },
];

export function Island() {
  const { route } = useLocation();
  const { params } = useRoute();
  const letterId = Number(params.id);

  const [letter, setLetter] = useState<LetterSummary | null>(null);
  const [progress, setProgress] = useState<StageProgress[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!letterId) return;
    (async () => {
      try {
        const [lResp, pResp, wResp] = await Promise.all([
          api<{ ok: true; letter: LetterSummary }>(`/api/letters/${letterId}`),
          currentUser.value
            ? api<{ ok: true; progress: StageProgress[] }>(
                `/api/progress/${currentUser.value.id}`,
              )
            : Promise.resolve({ progress: [] as StageProgress[] }),
          api<{ ok: true; words: Word[] }>(
            `/api/letters/${letterId}/words`,
          ).catch(() => ({ words: [] as Word[] })),
        ]);
        setLetter(lResp.letter);
        const pp = (pResp as { progress: StageProgress[] }).progress.filter(
          (r) => r.letter_id === letterId,
        );
        setProgress(pp);

        // Auto-play the letter NAME on entry (e.g. "أَلِف" for ا). The
        // short vowel SOUND ("أَ") is surfaced as a separate button below.
        unlockAudio();
        setTimeout(() => void playUrl(letterNameUrl(lResp.letter.id)), 300);

        // Warm the audio cache for everything the stages will need:
        //   - letter name + isolated sound (Meet, Draw, Hear, Say)
        //   - every word audio for this letter (Garden)
        // Fire-and-forget; playUrl() hits the decoded-buffer cache on demand.
        prefetchAudio([
          letterNameUrl(lResp.letter.id),
          letterSoundUrl(lResp.letter.id),
          ...((wResp as { words: Word[] }).words ?? []).map((w) => w.audio_url),
        ]);
      } catch (e) {
        setErr(String(e));
      }
    })();
  }, [letterId]);

  if (err) {
    return <ErrorState message={err} />;
  }
  if (!letter) {
    return <IslandSkeleton />;
  }

  const starsByStage = new Map(progress.map((p) => [p.stage, p.stars]));
  const hasAnyStar = progress.some((p) => p.stars > 0);

  return (
    <div class="flex-1 flex flex-col items-center px-4 py-6 pb-24 max-w-md mx-auto w-full gap-6">
      <NavButton
        ar="← الرُّجُوعُ"
        en="Back to map"
        onClick={() => route('/map')}
        class="self-start"
      />

      <div class="text-center">
        <p
          class="font-display text-coral drop-shadow-sm select-none"
          style={{
            fontSize: 'clamp(120px, 40vw, 260px)',
            lineHeight: '1.15',
            paddingTop: '0.08em',
          }}
        >
          {letter.letter}
        </p>
        <p class="text-xl font-body font-bold text-ink mt-2">{letter.name_ar}</p>
        <p class="text-sm text-ink/50" dir="ltr">
          {letter.name_en}
        </p>
      </div>

      <div class="flex gap-3">
        <button
          type="button"
          onClick={() => void playUrl(letterNameUrl(letter.id))}
          class="min-h-[60px] px-5 py-3 rounded-2xl bg-ocean text-white font-display shadow-lg shadow-ocean/30 active:scale-95 flex flex-col items-center"
        >
          <span class="text-lg">🔊 الاِسْمُ</span>
          <span class="text-[10px] opacity-75" dir="ltr">Name</span>
        </button>
        <button
          type="button"
          onClick={() => void playUrl(letterSoundUrl(letter.id))}
          class="min-h-[60px] px-5 py-3 rounded-2xl bg-coral text-white font-display shadow-lg shadow-coral/30 active:scale-95 flex flex-col items-center"
        >
          <span class="text-lg">🔊 الصَّوْتُ</span>
          <span class="text-[10px] opacity-75" dir="ltr">Sound</span>
        </button>
      </div>

      <div class="w-full space-y-3">
        {STAGES.map((stage, idx) => {
          const stars = starsByStage.get(stage.id) ?? 0;
          const completed = stars > 0;
          const locked = stage.id === 5 && !hasAnyStar;

          const base =
            'relative w-full min-h-[84px] px-5 py-4 rounded-3xl flex items-center gap-4 overflow-hidden shadow-lg transition-transform';
          const tone = locked
            ? 'bg-ink/10 text-ink/40 cursor-not-allowed'
            : completed
              ? 'bg-leaf text-white active:scale-[0.98]'
              : `bg-gradient-to-br ${stage.gradient} text-ink active:scale-[0.98] hover:shadow-xl`;

          return (
            <button
              key={stage.id}
              type="button"
              disabled={locked}
              onClick={() => route(`/island/${letterId}/${stage.path}`)}
              class={`${base} ${tone}`}
              style={{ marginInlineStart: `${idx * 16}px` }}
            >
              {/* Decorative corner shape, matched to the stage type. */}
              {!locked && !completed && (
                <StageShape shape={stage.shape} />
              )}

              {/* Icon chip — circular, accent color when not done, sun
                  when completed (stays visible on the leaf-green tile). */}
              <span
                class={`relative z-10 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-2xl shadow-sm ring-2 ${
                  completed || locked
                    ? 'bg-white/30 text-white ring-white/30'
                    : `${stage.accent} ${stage.accentRing}`
                }`}
                aria-hidden="true"
              >
                {stage.icon}
              </span>

              <div class="relative z-10 flex-1 text-right">
                <div
                  class="font-body font-bold"
                  style={{ fontSize: '18px', lineHeight: '1.3' }}
                >
                  {stage.label_ar}
                </div>
                <div class="text-xs opacity-70" dir="ltr">
                  {stage.label_en}
                </div>
              </div>

              <div class="relative z-10 flex gap-0.5">
                {[1, 2, 3].map((n) => (
                  <span key={n} class={n <= stars ? 'text-sun' : 'opacity-20'}>
                    ⭐
                  </span>
                ))}
              </div>

              {locked && <span class="relative z-10 text-xl">🔒</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Decorative inline-SVG flourish in the right corner of each stage card.
 * Tiny, subtle, per-stage character; all strokes use currentColor tinted
 * to match the stage accent via a wrapping span.
 */
function StageShape({ shape }: { shape: StageMeta['shape'] }) {
  const common =
    'pointer-events-none absolute top-1 left-3 h-14 w-14 opacity-25 select-none';
  switch (shape) {
    case 'eye':
      return (
        <svg class={`${common} text-ocean`} viewBox="0 0 40 40" fill="none">
          <path
            d="M4 20 C12 8 28 8 36 20 C28 32 12 32 4 20 Z"
            stroke="currentColor"
            stroke-width="2"
          />
          <circle cx="20" cy="20" r="4" fill="currentColor" />
        </svg>
      );
    case 'lines':
      return (
        <svg class={`${common} text-sun`} viewBox="0 0 40 40" fill="none">
          <path
            d="M6 30 Q14 20 22 26 T36 14"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
          />
          <circle cx="36" cy="14" r="2.5" fill="currentColor" />
        </svg>
      );
    case 'waves':
      return (
        <svg class={`${common} text-coral`} viewBox="0 0 40 40" fill="none">
          <path
            d="M4 20 Q10 14 16 20 T28 20 T40 20"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            fill="none"
          />
          <path
            d="M4 28 Q10 22 16 28 T28 28 T40 28"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            fill="none"
            opacity="0.6"
          />
        </svg>
      );
    case 'bubble':
      return (
        <svg class={`${common} text-berry`} viewBox="0 0 40 40" fill="none">
          <path
            d="M6 10 C6 6 10 4 14 4 L28 4 C32 4 36 6 36 10 L36 22 C36 26 32 28 28 28 L20 28 L14 34 L14 28 C10 28 6 26 6 22 Z"
            stroke="currentColor"
            stroke-width="2"
          />
        </svg>
      );
    case 'seed':
      return (
        <svg class={`${common} text-leaf`} viewBox="0 0 40 40" fill="none">
          <path
            d="M20 34 V18"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
          />
          <path
            d="M20 18 C12 14 10 6 18 4 C22 10 22 14 20 18 Z"
            stroke="currentColor"
            stroke-width="2"
            fill="currentColor"
            fill-opacity="0.4"
          />
          <path
            d="M20 22 C26 18 32 20 32 28 C26 28 22 26 20 22 Z"
            stroke="currentColor"
            stroke-width="2"
            fill="currentColor"
            fill-opacity="0.3"
          />
        </svg>
      );
  }
}

function ErrorState({ message }: { message: string }) {
  return (
    <div class="flex-1 grid place-items-center p-6 text-center">
      <div>
        <p class="text-4xl mb-4">🐻</p>
        <p class="text-2xl font-display">حَاوِلْ مَرَّةً أُخْرَى!</p>
        <p class="text-xs text-ink/40 mt-3" dir="ltr">
          {message}
        </p>
      </div>
    </div>
  );
}
